import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listExclusiveActors,
  listPublicActors,
  createExclusiveActor,
  updateExclusiveActor,
  deleteExclusiveActor,
  reorderExclusiveActors,
} from './api';
import type { CreateExclusiveActorRequest, UpdateExclusiveActorRequest } from './types';

const EXCLUSIVE_ACTORS_QUERY_KEY = ['exclusive-actors'];
const PUBLIC_ACTORS_QUERY_KEY = ['public-actors'];

/**
 * 専属AI俳優一覧を取得するフック
 */
export function useExclusiveActors() {
  return useQuery({
    queryKey: EXCLUSIVE_ACTORS_QUERY_KEY,
    queryFn: listExclusiveActors,
  });
}

/**
 * 公開AI俳優一覧を取得するフック（read-only用）
 */
export function usePublicActors() {
  return useQuery({
    queryKey: PUBLIC_ACTORS_QUERY_KEY,
    queryFn: listPublicActors,
  });
}

/**
 * 専属AI俳優を作成するフック
 */
export function useCreateExclusiveActor() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateExclusiveActorRequest) => createExclusiveActor(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EXCLUSIVE_ACTORS_QUERY_KEY });
    },
  });
}

/**
 * 専属AI俳優を更新するフック
 */
export function useUpdateExclusiveActor() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      actorId,
      request,
    }: {
      actorId: string;
      request: UpdateExclusiveActorRequest;
    }) => updateExclusiveActor(actorId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EXCLUSIVE_ACTORS_QUERY_KEY });
    },
  });
}

/**
 * 専属AI俳優を削除するフック
 */
export function useDeleteExclusiveActor() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (actorId: string) => deleteExclusiveActor(actorId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EXCLUSIVE_ACTORS_QUERY_KEY });
    },
  });
}

/**
 * 専属AI俳優の表示順を更新するフック
 */
export function useReorderExclusiveActors() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (order: { id: string; display_order: number }[]) => reorderExclusiveActors(order),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EXCLUSIVE_ACTORS_QUERY_KEY });
    },
  });
}
